// src/components/TimeSlotSelector.tsx
import React from 'react';

interface TimeSlotSelectorProps {
  loading: boolean;
  isRestaurantClosed: boolean;
  timeSlots: string[];
  selectedTime: string;
  onTimeSelect: (time: string) => void;
  selectedDate: string;
  workingHours: any;
}

/**
 * Determines appropriate message when no time slots are available
 * Based on current time and restaurant hours configuration
 */
const getMessage = (selectedDate: string, workingHours: any): string => {
  // Get current time
  const now = new Date();
  const currentHour = now.getHours(); // 0-23
  
  // Get day of week from selected date
  const [year, month, day] = selectedDate.split('-').map(Number);
  const dayIndex = new Date(year, month - 1, day).getDay(); // 0-6
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayName = dayNames[dayIndex];
  
  // Extract hours configuration from Franchises API
  const hours = workingHours;
  if (!hours) {
    return "⚠️ Operating hours not available. Please contact the restaurant.";
  }
  
  const dayHours = hours[dayName];
  if (!dayHours) {
    return "⚠️ Hours not configured for this day.";
  }
  
  // Check service availability
  const hasLunch = !!(dayHours.firstHalf?.start && dayHours.firstHalf?.end);
  const hasDinner = !!(dayHours.secondHalf?.start && dayHours.secondHalf?.end);
  
  // Parse service times (24-hour format)
  const lunchStart = hasLunch ? parseInt(dayHours.firstHalf.start.split(':')[0]) : null;
  const lunchEnd = hasLunch ? parseInt(dayHours.firstHalf.end.split(':')[0]) : null;
  const dinnerStart = hasDinner ? parseInt(dayHours.secondHalf.start.split(':')[0]) : null;
  const dinnerEnd = hasDinner ? parseInt(dayHours.secondHalf.end.split(':')[0]) : null;
  
  // Helper to format time
  const formatTime = (time24: string) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };
  
  // CASE 1: No services configured at all
  if (!hasLunch && !hasDinner) {
    return "⚠️ Operating hours are not set at the moment. Please contact the restaurant to check table availability.";
  }
  
  // CASE 2: Only Lunch Available
  if (hasLunch && !hasDinner) {
    if (currentHour < lunchStart!) {
      return `⏰ Lunch reservations available from ${formatTime(dayHours.firstHalf.start)}.`;
    }
    if (currentHour >= lunchStart! && currentHour < lunchEnd!) {
      return "⚠️ No tables available. Fully booked for lunch service.";
    }
    return `🕐 Lunch service ended at ${formatTime(dayHours.firstHalf.end)}. We're closed for the day.`;
  }
  
  // CASE 3: Only Dinner Available
  if (!hasLunch && hasDinner) {
    if (currentHour < dinnerStart!) {
      return `⏰ Dinner reservations available from ${formatTime(dayHours.secondHalf.start)}.`;
    }
    if (currentHour >= dinnerStart! && currentHour < dinnerEnd!) {
      return "⚠️ No tables available. Fully booked for dinner service.";
    }
    return `🕐 Dinner service ended at ${formatTime(dayHours.secondHalf.end)}. We're closed for the day.`;
  }
  
  // CASE 4: Both Lunch AND Dinner Available
  if (hasLunch && hasDinner) {
    // Before lunch
    if (currentHour < lunchStart!) {
      return `⏰ Lunch reservations available from ${formatTime(dayHours.firstHalf.start)}.`;
    }
    
    // During lunch hours
    if (currentHour >= lunchStart! && currentHour < lunchEnd!) {
      return "⚠️ No tables available. Fully booked for lunch service.";
    }
    
    // Between lunch and dinner
    if (currentHour >= lunchEnd! && currentHour < dinnerStart!) {
      return `⏰ Dinner reservations available from ${formatTime(dayHours.secondHalf.start)}.`;
    }
    
    // During dinner hours
    if (currentHour >= dinnerStart! && currentHour < dinnerEnd!) {
      return "⚠️ No tables available. Fully booked for dinner service.";
    }
    
    // After all service
    return `🕐 Service has ended for today. Last seating was at ${formatTime(dayHours.secondHalf.end)}.`;
  }
  
  // DEFAULT FALLBACK
  return "⚠️ No time slots available for this date.";
};

export default function TimeSlotSelector({
  loading,
  isRestaurantClosed,
  timeSlots,
  selectedTime,
  onTimeSelect,
  selectedDate,
  workingHours
}: TimeSlotSelectorProps) {
  
  // PRIORITY 1: Show Loading State
  if (loading) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {[...Array(12)].map((_, index) => (
          <div
            key={index}
            className="h-12 bg-gray-200 dark:bg-gray-600 rounded-lg animate-pulse"
          />
        ))}
      </div>
    );
  }
  
  // PRIORITY 2: Show Closed Message
  if (isRestaurantClosed) {
    const dayName = new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
    return (
      <div className="p-4 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-700">
        <p className="text-sm text-red-700 dark:text-red-400">
          🚫 We're closed on {dayName}. Please choose another day — we'd love to have you dine with us soon!
        </p>
      </div>
    );
  }
  
  // PRIORITY 3: Show Time Slots (HIGHEST PRIORITY if available)
  if (timeSlots && timeSlots.length > 0) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {timeSlots.map((slot: any) => {
          const time = typeof slot === 'string' ? slot : slot.start_time;
          const isAvailable = typeof slot === 'string' ? true : slot.isAvailable !== false;
          const isSelected = selectedTime === time;
          
          return (
            <button
              key={time}
              type="button"
              onClick={() => isAvailable && onTimeSelect(time)}
              disabled={!isAvailable}
              className={`p-3 rounded-lg font-semibold transition-all duration-200 border-2 ${
                !isAvailable
                  ? 'bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed line-through'
                  : isSelected
                  ? 'bg-[#EF4444] border-[#EF4444] text-white'
                  : 'border-gray-300 dark:border-gray-600 text-gray-800 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500'
              }`}
            >
              {time}
            </button>
          );
        })}
      </div>
    );
  }
  
  // PRIORITY 4: No Slots - Show Contextual Message
  const message = getMessage(selectedDate, workingHours);
  return (
    <div className="p-4 bg-orange-50 dark:bg-orange-900/30 rounded-lg border-2 border-orange-200 dark:border-orange-700">
      <p className="text-sm text-orange-700 dark:text-orange-400 text-center">
        {message}
      </p>
    </div>
  );
}
